import { OverlayModal, OverlayModalFooterProps, OverlayModalHeaderProps } from "goi_common";
import { Box } from "@chakra-ui/react";
import { call, ColorIcon, colors, customEvent, GoiLogo, HStack, Spacing, VStack } from "goi_common";
import { useRouter } from "next/router";

export default function ParlorRegionDiscountModalContent({
  regionName,
  discountParlors,
  eventPath = "parlor_region",
  onClose,
}: {
  eventPath?: "parlor_region" | "parlor_detail";
  regionName: string;
  discountParlors: Array<any>;
  onClose: () => void;
}) {
  const router = useRouter();
  return (
    <VStack width="100%" gap={10}>
      <VStack gap={12} width="100%">
        <Box
          textAlign="center"
          className="subtitle_21_b gray900"
          padding="20px 24px"
          wordBreak="keep-all"
        >
          장례식장 빈소 이용료,
          <br />
          고이 제휴 장례식장에서 할인받으세요.
        </Box>

        <VStack gap="8px" padding="0 16px">
          <Box
            width="fit-content"
            background="rgba(0, 0, 0, 0.5)"
            backdropFilter="blur(2px)"
            borderRadius="18px"
            padding="2px 10px 2px 2px"
          >
            <HStack gap="4px" alignItems="center" justifyContent="center">
              <Box margin="auto" background={colors.gray900} padding="4px" borderRadius="50%">
                <ColorIcon
                  name="icon-medal-check-mono"
                  color={colors.reviewYellow}
                  width={16}
                  height={16}
                />
              </Box>
              <HStack gap="6px" alignItems="center" justifyContent="center">
                <GoiLogo color={colors.white} size={16} />
                <span className="button_12_sb white">빈소 할인 제휴 장례식장</span>
              </HStack>
            </HStack>
          </Box>
          <Box as="h2" className="body_14_m gray600" py="6px">
            {regionName}에는{" "}
            <span className="gray900">
              {discountParlors[0].memo} 등 총 {discountParlors.length}곳의 고이 제휴 장례식장
            </span>
            이 있어요.
            <br />
            고객님의 상황에 맞는 가까운 할인 가능한 장례식장을 부담없이 문의하여 구체적으로
            알아보세요.
          </Box>

          <Box>
            <HStack
              width="100%"
              alignItems="center"
              justifyContent="space-between"
              padding="12px 16px"
              onClick={() => {
                customEvent({
                  newGtm: {
                    path: eventPath,
                    trigger_and_target: "modal_discount_parlor-uv_parlor_detail",
                  },
                });
                router.push(`/parlor/${discountParlors[0].parlor_id}/`);
                onClose();
              }}
              background={colors.green50}
              css={{
                border: `1px solid ${colors.green300}`,
                borderRadius: "6px",
                cursor: "pointer",
              }}
            >
              <HStack>
                <VStack justifyContent="center" width="100%" gap={3}>
                  <div className="subtitle_14_b gray800">{discountParlors[0].memo}</div>
                  <div className="green500 subtitle_14_b">{discountParlors[0].description} </div>
                </VStack>
              </HStack>
              <div>
                <ColorIcon
                  name="icon-arrow-right-small-mono"
                  width="36px"
                  color={colors.green400}
                />
              </div>
            </HStack>
          </Box>
        </VStack>
        <Box background={colors.warmGray100} padding="16px 24px" wordBreak="keep-all">
          <VStack className="subtitle_14_b gray700">
            <Box>
              고객님을 대신하여 장례식장 비용을 협의하고 최대한 합리적으로 장례를 진행할 수 있게
              도와드려요.
            </Box>
            <Box className="caption_12_b gray500" mt="8px">
              <span className="green500">반드시 장례지도사와 상담하여</span> 실제 할인 가능 여부를
              안내받으세요.
            </Box>
          </VStack>
        </Box>
      </VStack>
      <IntroductionItem />
    </VStack>
  );
}

const IntroductionItem = () => {
  return (
    <VStack padding="0 0 32px">
      <Spacing gap={16} />
      <Box as="article">
        <Box
          as="header"
          position="relative"
          margin="0 24px -15px"
          className={`caption_12_b orange650`}
          width="fit-content"
          background={colors.white}
          border={`1px solid ${colors.orange500}`}
          borderRadius="10px"
          padding="4px 12px"
        >
          유의사항
        </Box>
        <Box
          as="ul"
          padding="24px 32px"
          className={`body_14_m orange700`}
          background={colors.orange50}
          whiteSpace="pre-line"
        >
          <li>
            - 고이 상담 채널이 아닌 기타 채널(장례식장 직통 상담 등)을 통하여 상담을 받은 경우, 빈소
            이용료 할인을 보장하지 않습니다.
          </li>
          <br />
          <li>
            - 안내된 할인율은{" "}
            <b>
              고이 상담을 통하여 고이 서비스를 이용하시고, 장례를 진행하는 경우 적용가능한 혜택으로
            </b>{" "}
            제휴 장례식장에서 실제로 할인받을 수 있는 빈소 이용료(가족장 기준) 할인의 최대치입니다.
          </li>
          <br />
          <li>
            - 1. 장례식장 빈소 상황 2. 조문객 수, 서비스에 따라 실제 할인율이 달라집니다. 반드시
            장례지도사와 상담하여 안내를 받으세요.
          </li>
          <br />
        </Box>
      </Box>
    </VStack>
  );
};

const ParlorRegionDiscountModalContentHeader = ({
  onClose,
  eventPath = "parlor_region",
}: {
  onClose: () => void;
  eventPath?: "parlor_detail" | "parlor_region";
}) => {
  const headerProps: OverlayModalHeaderProps = {
    item: {
      title: "",
      onClick: () => {
        onClose();
      },
      closeEvent: {
        newGtm: {
          path: eventPath,
          trigger_and_target: "modal_discount_parlor-close",
        },
      },
    },
  };
  return <OverlayModal.Header.CloseBtnType props={headerProps} />;
};
ParlorRegionDiscountModalContent.header = ParlorRegionDiscountModalContentHeader;

function ParlorRegionDiscountModalContentFooter({
  onClose,
  eventPath = "parlor_region",
}: {
  onClose: () => void;
  eventPath?: "parlor_region" | "parlor_detail";
}) {
  const footerProps: OverlayModalFooterProps = {
    item: [
      {
        text: <>닫기</>,
        onClick: () => {
          onClose();
        },
        width: "50%",
        event: {
          newGtm: {
            path: eventPath,
            trigger_and_target: "modal_discount_parlor-close",
          },
        },
      },

      {
        text: (
          <HStack gap={8} width="100%" alignItems="center" justifyContent="center">
            <ColorIcon name="icon-call-mono" color={colors.white} width="16px" height="16px" />
            <Box>문의하기</Box>
          </HStack>
        ),
        onClick: () => {
          call("1666-9784");
          if (eventPath === "parlor_detail") {
            customEvent({
              newGtm: {
                path: eventPath,
                trigger_and_target: "modal_detail_CTA",
              },
            });
          } else {
            customEvent({
              newGtm: {
                path: eventPath,
                trigger_and_target: "modal_discount-CTA",
              },
            });
          }
        },
      },
    ],
  };

  return <OverlayModal.Footer.TwinBtnType props={footerProps} />;
}

ParlorRegionDiscountModalContent.footer = ParlorRegionDiscountModalContentFooter;
